import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { documentFoldersApi, pdvApi, documentsApi } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Folder,
  FolderPlus,
  FileText,
  Upload,
  Trash2,
  ChevronRight,
  ChevronDown,
  Plus,
  Calendar,
  AlertTriangle,
  Download,
  Move,
  Search,
  X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const StatusBadge = ({ status }) => {
  const variants = {
    valid: 'bg-emerald-100 text-emerald-800',
    expiring: 'bg-amber-100 text-amber-800',
    expired: 'bg-rose-100 text-rose-800'
  };
  const labels = {
    valid: 'Valido',
    expiring: 'In Scadenza',
    expired: 'Scaduto'
  };
  return (
    <Badge className={variants[status] || variants.valid}>
      {labels[status] || status}
    </Badge>
  );
};

const FolderCard = ({ folder, isExpanded, onToggle, onAddSubfolder, onUpload, children }) => (
  <div className="border border-border rounded-lg mb-3 overflow-hidden">
    <div
      className="flex items-center justify-between p-4 bg-muted/50 cursor-pointer hover:bg-muted transition-colors"
      onClick={() => onToggle(folder.id)}
    >
      <div className="flex items-center gap-3">
        {isExpanded ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
        <Folder className="w-5 h-5 text-amber-500" />
        <span className="font-medium">{folder.name}</span>
        <Badge variant="secondary" className="ml-2">{folder.total_documents_count || 0} file</Badge>
      </div>
      <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
        <Button variant="ghost" size="sm" onClick={() => onAddSubfolder(folder.id)}>
          <FolderPlus className="w-4 h-4 mr-1" />
          Sottocartella
        </Button>
        <Button variant="ghost" size="sm" onClick={() => onUpload(folder.id, null)}>
          <Upload className="w-4 h-4 mr-1" />
          Carica
        </Button>
      </div>
    </div>
    {isExpanded && (
      <div className="p-4 bg-background">
        {children}
      </div>
    )}
  </div>
);

const SubfolderRow = ({ subfolder, folderId, onUpload, onDelete, onClick, isSelected }) => (
  <div
    className={`flex items-center justify-between p-3 rounded-md cursor-pointer transition-colors ${isSelected ? 'bg-primary/10 border border-primary' : 'hover:bg-muted'}`}
    onClick={() => onClick(subfolder)}
  >
    <div className="flex items-center gap-3">
      <Folder className="w-4 h-4 text-blue-500" />
      <span>{subfolder.name}</span>
      <Badge variant="outline" className="text-xs">{subfolder.documents_count || 0}</Badge>
    </div>
    <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onUpload(folderId, subfolder.id)}>
        <Upload className="w-4 h-4" />
      </Button>
      <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => onDelete(subfolder)}>
        <Trash2 className="w-4 h-4" />
      </Button>
    </div>
  </div>
);

const DocumentRow = ({ doc, onMove, onDelete, onEdit }) => (
  <div className="flex items-center justify-between p-3 border border-border rounded-md hover:bg-muted/50 transition-colors">
    <div className="flex items-center gap-3 flex-1 min-w-0">
      <FileText className="w-5 h-5 text-muted-foreground flex-shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="font-medium truncate">{doc.name}</p>
        {doc.expiry_date && (
          <p className="text-xs text-muted-foreground flex items-center gap-1">
            <Calendar className="w-3 h-3" />
            Scade: {new Date(doc.expiry_date).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' })}
          </p>
        )}
      </div>
    </div>
    <div className="flex items-center gap-2">
      {doc.expiry_date && <StatusBadge status={doc.status} />}
      <div className="flex items-center gap-1">
        {doc.file_url && (
          <Button variant="ghost" size="icon" className="h-8 w-8" asChild>
            <a href={doc.file_url} target="_blank" rel="noopener noreferrer">
              <Download className="w-4 h-4" />
            </a>
          </Button>
        )}
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onMove(doc)}>
          <Move className="w-4 h-4" />
        </Button>
        <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => onDelete(doc)}>
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  </div>
);

export default function PDVDocumentsPage() {
  const { id: pdvId } = useParams();
  const { user } = useAuth();
  const isAzienda = user?.role === 'azienda';
  const [pdv, setPdv] = useState(null);
  const [folders, setFolders] = useState([]);
  const [expandedFolders, setExpandedFolders] = useState({});
  const [selectedSubfolder, setSelectedSubfolder] = useState(null);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  
  // Dialogs
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [subfolderDialogOpen, setSubfolderDialogOpen] = useState(false);
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  
  // Form states
  const [uploadTargetFolder, setUploadTargetFolder] = useState(null);
  const [uploadTargetSubfolder, setUploadTargetSubfolder] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [newSubfolderName, setNewSubfolderName] = useState('');
  const [newSubfolderParent, setNewSubfolderParent] = useState(null);
  const [docToMove, setDocToMove] = useState(null);
  const [moveTargetFolder, setMoveTargetFolder] = useState('');
  const [moveTargetSubfolder, setMoveTargetSubfolder] = useState('');
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleteType, setDeleteType] = useState(''); // 'document', 'subfolder'

  const loadData = useCallback(async () => {
    try {
      const [pdvRes, foldersRes] = await Promise.all([
        pdvApi.getOne(pdvId),
        documentFoldersApi.getFolders(pdvId)
      ]);
      setPdv(pdvRes.data);
      setFolders(foldersRes.data);
      
      // Auto-expand first folder if exists
      if (foldersRes.data.length > 0 && Object.keys(expandedFolders).length === 0) {
        setExpandedFolders({ [foldersRes.data[0].id]: true });
      }
    } catch (error) {
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  }, [pdvId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const loadFolderDocuments = async (folderId, subfolderId = null) => {
    try {
      const res = await documentFoldersApi.getFolderDocuments(folderId, subfolderId);
      setDocuments(res.data);
      setSelectedFolder(folderId);
      setSelectedSubfolder(subfolderId);
    } catch (error) {
      toast.error('Errore nel caricamento dei documenti');
    }
  };

  const toggleFolder = (folderId) => {
    setExpandedFolders(prev => ({
      ...prev,
      [folderId]: !prev[folderId]
    }));
    
    // Load documents when expanding
    if (!expandedFolders[folderId]) {
      loadFolderDocuments(folderId, null);
    }
  };

  const handleAddSubfolder = (folderId) => {
    setNewSubfolderParent(folderId);
    setNewSubfolderName('');
    setSubfolderDialogOpen(true);
  };

  const handleCreateSubfolder = async () => {
    if (!newSubfolderName.trim()) return;
    try {
      await documentFoldersApi.createSubfolder(newSubfolderParent, {
        folder_id: newSubfolderParent,
        name: newSubfolderName.trim()
      });
      toast.success('Sottocartella creata');
      setSubfolderDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error('Errore nella creazione');
    }
  };

  const handleUploadClick = (folderId, subfolderId) => {
    setUploadTargetFolder(folderId);
    setUploadTargetSubfolder(subfolderId);
    setSelectedFiles([]);
    setUploadDialogOpen(true);
  };

  const handleFilesSelected = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles(files);
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;
    setUploading(true);
    try {
      const res = await documentFoldersApi.batchUpload(
        pdvId,
        uploadTargetFolder,
        uploadTargetSubfolder,
        selectedFiles
      );
      
      if (res.data.total_uploaded > 0) {
        toast.success(`${res.data.total_uploaded} file caricati`);
      }
      if (res.data.total_errors > 0) {
        toast.error(`${res.data.total_errors} errori durante il caricamento`);
      }
      
      setUploadDialogOpen(false);
      loadData();
      
      // Reload current view
      if (selectedFolder) {
        loadFolderDocuments(selectedFolder, selectedSubfolder);
      }
    } catch (error) {
      toast.error('Errore durante il caricamento');
    } finally {
      setUploading(false);
    }
  };

  const handleMoveDocument = (doc) => {
    setDocToMove(doc);
    setMoveTargetFolder(doc.folder_id || '');
    setMoveTargetSubfolder(doc.subfolder_id || '');
    setMoveDialogOpen(true);
  };

  const handleMove = async () => {
    if (!docToMove) return;
    try {
      await documentFoldersApi.moveDocument(
        docToMove.id,
        moveTargetFolder || null,
        moveTargetSubfolder || null
      );
      toast.success('Documento spostato');
      setMoveDialogOpen(false);
      loadData();
      
      if (selectedFolder) {
        loadFolderDocuments(selectedFolder, selectedSubfolder);
      }
    } catch (error) {
      toast.error('Errore nello spostamento');
    }
  };

  const handleDeleteClick = (item, type) => {
    if (isAzienda) {
      toast.error(
        "Per cancellare questo file devi essere autorizzato dall'amministratore. Contattalo per procedere.",
        { duration: 6000 }
      );
      return;
    }
    setItemToDelete(item);
    setDeleteType(type);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;
    try {
      if (deleteType === 'document') {
        await documentsApi.delete(itemToDelete.id);
        toast.success('Documento eliminato');
      } else if (deleteType === 'subfolder') {
        await documentFoldersApi.deleteSubfolder(itemToDelete.id);
        toast.success('Sottocartella eliminata');
      }
      setDeleteDialogOpen(false);
      loadData();
      
      if (selectedFolder) {
        loadFolderDocuments(selectedFolder, selectedSubfolder);
      }
    } catch (error) {
      toast.error('Errore durante l\'eliminazione');
    }
  };

  // Calculate stats
  const totalDocs = folders.reduce((sum, f) => sum + (f.total_documents_count || 0), 0);
  const allDocs = [];
  // We'd need to fetch all docs to calculate expiring, but for now just show folder counts

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      data-testid="pdv-documents-page"
    >
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Link to={`/pdv/${pdvId}`}>
          <Button variant="ghost" size="icon" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
        </Link>
        <div className="flex-1">
          <h1 className="page-title">Documenti - {pdv?.name}</h1>
          <p className="text-muted-foreground">{totalDocs} documenti in {folders.length} cartelle</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {folders.slice(0, 4).map((folder) => (
          <Card key={folder.id} className="hover-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Folder className="w-8 h-8 text-amber-500" />
                <div>
                  <p className="text-2xl font-bold">{folder.total_documents_count || 0}</p>
                  <p className="text-xs text-muted-foreground truncate">{folder.name}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Folders List */}
      <div className="space-y-2">
        {folders.map((folder) => (
          <FolderCard
            key={folder.id}
            folder={folder}
            isExpanded={expandedFolders[folder.id]}
            onToggle={toggleFolder}
            onAddSubfolder={handleAddSubfolder}
            onUpload={handleUploadClick}
          >
            {/* Subfolders */}
            {folder.subfolders && folder.subfolders.length > 0 && (
              <div className="space-y-2 mb-4">
                <p className="text-sm font-medium text-muted-foreground mb-2">Sottocartelle</p>
                {folder.subfolders.map((sf) => (
                  <SubfolderRow
                    key={sf.id}
                    subfolder={sf}
                    folderId={folder.id}
                    isSelected={selectedSubfolder === sf.id && selectedFolder === folder.id}
                    onClick={() => loadFolderDocuments(folder.id, sf.id)}
                    onUpload={handleUploadClick}
                    onDelete={(sf) => handleDeleteClick(sf, 'subfolder')}
                  />
                ))}
              </div>
            )}

            {/* Documents in folder root */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-muted-foreground">
                  {selectedSubfolder && selectedFolder === folder.id
                    ? `Documenti in sottocartella`
                    : `Documenti nella cartella`
                  }
                </p>
                {selectedSubfolder && selectedFolder === folder.id && (
                  <Button variant="ghost" size="sm" onClick={() => loadFolderDocuments(folder.id, null)}>
                    <X className="w-4 h-4 mr-1" /> Mostra tutti
                  </Button>
                )}
              </div>
              
              {documents.length > 0 && selectedFolder === folder.id ? (
                documents.map((doc) => (
                  <DocumentRow
                    key={doc.id}
                    doc={doc}
                    onMove={handleMoveDocument}
                    onDelete={(doc) => handleDeleteClick(doc, 'document')}
                    onEdit={() => {}}
                  />
                ))
              ) : selectedFolder === folder.id ? (
                <div className="text-center py-8 text-muted-foreground">
                  <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>Nessun documento</p>
                  <Button variant="outline" size="sm" className="mt-2" onClick={() => handleUploadClick(folder.id, selectedSubfolder)}>
                    <Upload className="w-4 h-4 mr-1" /> Carica file
                  </Button>
                </div>
              ) : (
                <div className="text-center py-4 text-muted-foreground text-sm">
                  Clicca sulla cartella per vedere i documenti
                </div>
              )}
            </div>
          </FolderCard>
        ))}
      </div>

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Carica Documenti</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Seleziona file (multipli)</Label>
              <Input
                type="file"
                multiple
                onChange={handleFilesSelected}
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.dwg,.dxf,.xls,.xlsx"
                data-testid="upload-files-input"
              />
            </div>
            
            {selectedFiles.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium">{selectedFiles.length} file selezionati:</p>
                <div className="max-h-40 overflow-y-auto space-y-1">
                  {selectedFiles.map((file, idx) => (
                    <div key={idx} className="text-sm text-muted-foreground flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      {file.name}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setUploadDialogOpen(false)}>
                Annulla
              </Button>
              <Button onClick={handleUpload} disabled={uploading || selectedFiles.length === 0}>
                {uploading ? 'Caricamento...' : `Carica ${selectedFiles.length} file`}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* New Subfolder Dialog */}
      <Dialog open={subfolderDialogOpen} onOpenChange={setSubfolderDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nuova Sottocartella</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Nome sottocartella</Label>
              <Input
                value={newSubfolderName}
                onChange={(e) => setNewSubfolderName(e.target.value)}
                placeholder="Es: Certificati 2024"
                data-testid="subfolder-name-input"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setSubfolderDialogOpen(false)}>
                Annulla
              </Button>
              <Button onClick={handleCreateSubfolder} disabled={!newSubfolderName.trim()}>
                Crea
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Move Document Dialog */}
      <Dialog open={moveDialogOpen} onOpenChange={setMoveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Sposta Documento</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Spostare "{docToMove?.name}" in:
            </p>
            
            <div className="space-y-2">
              <Label>Cartella</Label>
              <Select value={moveTargetFolder} onValueChange={(v) => { setMoveTargetFolder(v); setMoveTargetSubfolder(''); }}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleziona cartella" />
                </SelectTrigger>
                <SelectContent>
                  {folders.map((f) => (
                    <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {moveTargetFolder && (
              <div className="space-y-2">
                <Label>Sottocartella (opzionale)</Label>
                <Select value={moveTargetSubfolder} onValueChange={setMoveTargetSubfolder}>
                  <SelectTrigger>
                    <SelectValue placeholder="Root della cartella" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">Root della cartella</SelectItem>
                    {folders.find(f => f.id === moveTargetFolder)?.subfolders?.map((sf) => (
                      <SelectItem key={sf.id} value={sf.id}>{sf.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setMoveDialogOpen(false)}>
                Annulla
              </Button>
              <Button onClick={handleMove}>
                Sposta
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Conferma eliminazione</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteType === 'document' 
                ? `Sei sicuro di voler eliminare "${itemToDelete?.name}"? Questa azione non può essere annullata.`
                : `Sei sicuro di voler eliminare la sottocartella "${itemToDelete?.name}"? I documenti verranno spostati nella cartella principale.`
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteConfirm}
              className="bg-destructive hover:bg-destructive/90"
            >
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </motion.div>
  );
}
